/**
 * 10.9BE — ANÚNCIOS RECOMPENSADOS (opcionais, o jogador escolhe assistir).
 *
 * O jogo só pergunta `Ads.show(placement)` → "assistiu até o fim?". Quem exibe é um PROVEDOR:
 *   · `SimulatedAdProvider` (hoje, fase de testes): overlay com contagem de 5 s — prova o fluxo.
 *   · AdMob (APK, quando tiver conta + app na Play) — trocar `Ads.provider`, o resto não muda.
 *
 * Regras (decisão do Thiago): VIP NUNCA vê anúncio — ganha o benefício direto (argumento de venda
 * do VIP); nada de anúncio forçado; limite DIÁRIO por placement salvo no progresso.
 */
import { Vip } from "../progression/Vip";

export type AdPlacement = "revive" | "dailyDouble" | "freeDiamonds" | "xpBoost" | "zoneSkip" | "chestReroll" | "trainDouble";

/** Limite diário por placement (0 = sem limite). */
export const AD_DAILY_CAP: Record<AdPlacement, number> = {
  revive: 0, // ILIMITADO (Thiago: "a pessoa assiste se quiser reviver — imagina morrer no meio do boss")
  dailyDouble: 1,
  freeDiamonds: 5,
  xpBoost: 3,
  zoneSkip: 3,
  chestReroll: 2,
  trainDouble: 1, // 10.9BY — dobra o XP de UMA coleta de treino por dia
};

export interface AdProvider {
  /** Resolve true se o vídeo foi assistido até o fim (recompensa liberada). */
  showRewarded(placement: AdPlacement): Promise<boolean>;
}

/** Contadores diários persistidos (o Game liga ao GameProgress). */
export interface AdCounters {
  day: string;
  used: Record<string, number>;
}

function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Provedor SIMULADO: overlay escuro com contagem regressiva; "Pular" = não recompensa. */
export class SimulatedAdProvider implements AdProvider {
  private root: HTMLDivElement | null = null;
  constructor(private readonly uiRoot: HTMLElement, private readonly seconds = 5) {}

  showRewarded(placement: AdPlacement): Promise<boolean> {
    return new Promise((resolve) => {
      if (!this.root) {
        this.root = document.createElement("div");
        this.root.className = "ad-sim";
        this.uiRoot.appendChild(this.root);
      }
      const el = this.root;
      el.innerHTML = `
        <div class="ad-sim-box">
          <div class="ad-sim-tag">ANÚNCIO</div>
          <div class="ad-sim-video">📺</div>
          <div class="ad-sim-count"></div>
          <div class="ad-sim-place">${placement}</div>
          <button class="btn ad-sim-skip">Pular (sem recompensa)</button>
        </div>`;
      el.hidden = false;
      let left = this.seconds;
      const count = el.querySelector<HTMLElement>(".ad-sim-count")!;
      const tick = (): void => { count.textContent = left > 0 ? `Recompensa em ${left}s…` : "✓ Recompensa liberada"; };
      tick();
      const done = (ok: boolean): void => { window.clearInterval(iv); el.hidden = true; resolve(ok); };
      const iv = window.setInterval(() => { left--; tick(); if (left <= 0) window.setTimeout(() => done(true), 500); }, 1000);
      el.querySelector(".ad-sim-skip")!.addEventListener("click", () => done(false));
    });
  }
}

export const Ads = {
  provider: null as AdProvider | null,
  counters: { day: today(), used: {} } as AdCounters,
  /** chamado depois de cada mudança nos contadores (o Game salva) */
  onChange: (() => {}) as () => void,

  attach(counters: AdCounters | undefined, onChange: () => void): void {
    this.counters = counters && typeof counters === "object" && typeof counters.day === "string" ? { day: counters.day, used: { ...(counters.used ?? {}) } } : { day: today(), used: {} };
    this.onChange = onChange;
    this.rollDay();
  },

  rollDay(): void {
    const t = today();
    if (this.counters.day !== t) { this.counters = { day: t, used: {} }; this.onChange(); }
  },

  used(p: AdPlacement): number {
    this.rollDay();
    return this.counters.used[p] ?? 0;
  },
  left(p: AdPlacement): number {
    const cap = AD_DAILY_CAP[p];
    return cap === 0 ? 99 : Math.max(0, cap - this.used(p));
  },
  /** Há oferta pra esse placement agora? (VIP sempre tem — sem vídeo.) */
  available(p: AdPlacement): boolean {
    if (Vip.active) return true;
    return !!this.provider && this.left(p) > 0;
  },

  /**
   * Exibe o vídeo e devolve se a recompensa foi liberada. VIP: libera direto (sem vídeo) e NÃO
   * consome o limite diário… mas ainda respeita o cap pra não virar infinito (revive 3/dia).
   */
  async show(p: AdPlacement): Promise<boolean> {
    if (this.left(p) <= 0) return false;
    let ok: boolean;
    if (Vip.active) ok = true;
    else {
      if (!this.provider) return false;
      try { ok = await this.provider.showRewarded(p); } catch { ok = false; }
    }
    if (ok) { this.counters.used[p] = this.used(p) + 1; this.onChange(); }
    return ok;
  },
};
